import { useRef, useState } from "react";
import Autocomplete from "react-google-autocomplete";
import toast from "react-hot-toast";
import Styles from "./LocationInput.module.css";
import { DEFAULT_LOCATION, MapAPIKey } from "../../Utility/Utility";
import axios from "axios";

export default function LocationInput({
  errorText,
  onSelectAddress,
  defaultAddress = "",
}) {
  const inputRef = useRef(null);
  const [loadingLocation, setLoadingLocation] = useState(false);

  // Reverse geocode: lat/lng -> address
  const getAddressFromLatLng = async (lat, lng) => {
    if (!window.google) throw new Error("Google Maps not loaded");

    const geocoder = new window.google.maps.Geocoder();
    return new Promise((resolve, reject) => {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results && results.length > 0) {
          resolve(results[0].formatted_address);
        } else {
          reject(`Geocoder failed due to: ${status}`);
        }
      });
    });
  };

  // Forward geocode: address -> lat/lng
  const getLatLngFromAddress = async (address) => {
    try {
      const response = await axios.get(
        "https://maps.googleapis.com/maps/api/geocode/json",
        {
          params: {
            address,
            key: MapAPIKey,
          },
        },
      );
      const results = response.data.results;
      if (results && results.length > 0) {
        return results[0].geometry.location; // { lat, lng }
      } else {
        throw new Error("Address not found");
      }
    } catch (err) {
      console.error("Geocoding error:", err);
      throw new Error(err.message || "Geocoding failed");
    }
  };

  // Get current device location
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject("Unable to fetch location");
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          toast(error.message, { type: "error" });
          reject(error);
        },
      );
    });
  };

  // Handle "Use current location" button
  const handleCurrentLocation = async () => {
    try {
      setLoadingLocation(true);
      const position = await getCurrentLocation();
      const address = await getAddressFromLatLng(position.lat, position.lng);
      if (inputRef.current) inputRef.current.value = address;

      onSelectAddress({
        address,
        ...position,
      });
    } catch (err) {
      toast(err.message, { type: "error" });
    } finally {
      setLoadingLocation(false);
    }
  };

  // Handle manual input (user types address and presses "Go" or Enter)
  const handleManualAddress = async (event) => {
    if (event.key === "Enter") {
      const typedAddress = inputRef.current?.value;
      if (!typedAddress) return;

      try {
        setLoadingLocation(true);
        const latLng = await getLatLngFromAddress(typedAddress);
        onSelectAddress({
          address: typedAddress,
          lat: latLng.lat,
          lng: latLng.lng,
        });
      } catch (err) {
        toast(err.message || "Failed to get location", { type: "error" });
      } finally {
        setLoadingLocation(false);
      }
    }
  };

  return (
    <div className={Styles.mainContainer}>
      <label className="label">
        Delivery Location <span className="text-danger fs-5">*</span>
      </label>

      <div className={Styles.inputWrapper}>
        <Autocomplete
          apiKey={MapAPIKey}
          ref={inputRef}
          defaultValue={defaultAddress}
          placeholder="Select or type address..."
          className={Styles.googleInput}
          options={{
            types: ["geocode", "establishment"],
            componentRestrictions: { country: "in" },
          }}
          onPlaceSelected={(place) => {
            if (place.geometry) {
              // Normal selection from Google suggestion
              onSelectAddress({
                address: place.formatted_address,
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
              });
            }
            //  else {
            //   // fallback: typed text
            //   handleManualAddress({ key: "Enter" });
            // }
          }}
          // onKeyDown={handleManualAddress} // Handle "Go" / Enter press
        />

        <button
          type="button"
          className={Styles.locationBtn}
          onClick={handleCurrentLocation}
          disabled={loadingLocation}
          title="Use current location"
        >
          {loadingLocation ? "⏳" : "📍"}
        </button>
      </div>

      {errorText && (
        <div className={`${Styles.locationErrorMsg} text-danger`}>
          {errorText}
        </div>
      )}
    </div>
  );
}
